import { processDataService } from '../../../services/processDataService'
import {
  ACTIVITY_TYPES,
  FIELD_CONSTRAINTS,
  FIELD_TYPES,
  LOGIC_GATE,
  PROCESS_ACTIVITY_STATUSES,
  TABLE_FIELD_CONSTRAINTS,
} from '../../../utility/constants'
import {
  getConstraintErrorId,
  getConstraintErrorIdFromFieldId,
  getFieldId,
  getFieldIdFromKey,
  getLegacySubfieldId,
  getReviewId,
  getSelectableTableId,
} from './utils'
import { applyLogicGate, constraintValidationWithOperation } from './validationUtil'

export const isReviewSwitchDisabled = (activityType, activityStatus, processModifiedBy, userId) => {
  if (activityType === ACTIVITY_TYPES.LONG_RUNNING) {
    return userId == processModifiedBy
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY) {
    return true
  } else {
    return (
      activityType == ACTIVITY_TYPES.REVIEW && activityStatus === PROCESS_ACTIVITY_STATUSES.CLOSED
    )
  }
}

export const isFieldActive = (
  field,
  allValues,
  conditionalEnableDisable,
  activityType,
  activityStatus,
  version,
  previousProcessReviewData = {},
  reviewData,
  processModifiedBy,
  userId,
) => {
  if (activityType === ACTIVITY_TYPES.REVIEW) {
    return false
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version === 1) {
    if (field?.is_read_only) {
      return false
    } else {
      return (
        activityStatus === PROCESS_ACTIVITY_STATUSES.ACTIVE &&
        isConditionallyEnabled(field.id, allValues, conditionalEnableDisable)
      )
    }
  } else if (activityType === ACTIVITY_TYPES.LONG_RUNNING && version === 1) {
    const isEnabledConditionally = isConditionallyEnabled(
      field.id,
      allValues,
      conditionalEnableDisable,
    )

    const isReviewRejected =
      reviewData && reviewData[getReviewId(field.id)]
        ? reviewData[getReviewId(field.id)] == (false || null)
        : true

    if (field?.is_read_only) {
      return false
    } else {
      return activityStatus == PROCESS_ACTIVITY_STATUSES.ACTIVE && userId == processModifiedBy
        ? isEnabledConditionally && isReviewRejected
        : false
    }
  } else if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version > 1) {
    if (field?.is_read_only) {
      return false
    } else {
      return activityStatus == PROCESS_ACTIVITY_STATUSES.ACTIVE
        ? Object.keys(previousProcessReviewData).length > 0 &&
          Object.keys(previousProcessReviewData).includes(getReviewId(field.id)) &&
          previousProcessReviewData[getReviewId(field.id)] == (false || true)
          ? previousProcessReviewData[getReviewId(field.id)] == false &&
            isConditionallyEnabled(field.id, allValues, conditionalEnableDisable)
          : true
        : false
    }
  }
}

export const isConditionallyEnabled = (
  id,
  allValues,
  conditionalEnableDisable,
  fields = [],
  row,
) => {
  const sources = conditionalEnableDisable?.filter((it) => it.destination_field_template_id == id)
  if (sources?.length > 0) {
    const statuses = []
    for (const target of sources) {
      const sourceFieldId = target?.source_field_template_id
      const sourceField = fields?.find((it) => it.id == sourceFieldId)

      let fieldsProcessDataValue = allValues[getFieldId(target?.source_field_template_id)]
      if (sourceField?.is_subfield) {
        fieldsProcessDataValue = allValues[getLegacySubfieldId(sourceFieldId, row)]
      }

      const isEnabled = constraintValidationWithOperation(
        fieldsProcessDataValue,
        target?.enable_on_value,
        target?.operation,
      )

      statuses.push(isEnabled)
    }

    return applyLogicGate(statuses, LOGIC_GATE.AND)
  }
  return true
}

export const isFieldHidden = (field, hiddenFields = [], hiddenColumnIds = [], other = {}) => {
  const isHiddenForTeam = hiddenFields.find((it) => it.id == field.id)
  const isHiddenBySetting = hiddenColumnIds?.find((it) => it == field.id)

  let isHiddenByConditionalEnableDisable = !isConditionallyEnabled(
    field.id,
    other?.allValues,
    other?.conditionalEnableDisable,
    other?.fields,
    other?.row,
  )

  if (isHiddenByConditionalEnableDisable) {
    const target = other?.conditionalEnableDisable?.filter(
      (it) => it.destination_field_template_id == field.id,
    )
    if (target?.length > 0) {
      const is_destination_field_subfield = target[0].is_destination_field_subfield
      const is_source_field_subfield = target[0].is_source_field_subfield
      if (is_destination_field_subfield && is_source_field_subfield) {
        isHiddenByConditionalEnableDisable = false
      }
    }
  }

  const isFinallyHidden =
    field.is_hidden ||
    isHiddenForTeam != undefined ||
    isHiddenBySetting != undefined ||
    isHiddenByConditionalEnableDisable

  return isFinallyHidden
}

export const checkFieldConstraints = (
  field,
  value,
  constraints = [],
  allValues,
  rowNumber = null,
) => {
  let suffix = rowNumber ? `__${rowNumber}` : ''

  const validationArray = []

  // first check for required field validation
  if (field.is_required && (value == getDefaultValueForField(field) || !value)) {
    validationArray.push(`It's a required field!`)
  } else {
    const validations = constraints.filter((it) => it.field_template_id == field.id)
    if (!validations.length === 0) return validationArray // return empty if no constraints found!!
    for (let validation of validations) {
      let errorMessage = null
      // Getting Related Field Value Or Constant Value
      let relatedValue = 0
      if (validation.related_field_template_id != null && validation.constant_value == null) {
        relatedValue = allValues[`f_${validation.related_field_template_id}${suffix}`]
      } else if (
        validation.related_field_template_id == null &&
        validation.constant_value != null
      ) {
        relatedValue = validation.constant_value
      }

      switch (validation.constraint) {
        case FIELD_CONSTRAINTS.POSITIVE:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.POSITIVE)) {
            errorMessage = `${field.field_template_name} must be a positive number!`
          }
          break
        case FIELD_CONSTRAINTS.POSITIVE_INT:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.POSITIVE_INT)) {
            errorMessage = `${field.field_template_name} must be a positive integer!`
          }
          break
        case FIELD_CONSTRAINTS.INT:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.INT)) {
            errorMessage = `${field.field_template_name} must be an integer!`
          }
          break
        case FIELD_CONSTRAINTS.WHOLE_NUM:
          if (!constraintValidationWithOperation(value, null, FIELD_CONSTRAINTS.WHOLE_NUM)) {
            errorMessage = `${field.field_template_name} must be a whole number!`
          }
          break
        case FIELD_CONSTRAINTS.EQUAL:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.EQUAL)) {
            errorMessage = `${field.field_template_name} must be equal to ${relatedValue}!`
          }
          break
        case FIELD_CONSTRAINTS.LT:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.LT)) {
            errorMessage = `${field.field_template_name} must be less than ${relatedValue}!`
          }
          break
        case FIELD_CONSTRAINTS.LTE:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.LTE)) {
            errorMessage = `${field.field_template_name} must be less than or equal to ${relatedValue}!`
          }
          break
        case FIELD_CONSTRAINTS.GT:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.GT)) {
            errorMessage = `${field.field_template_name} must be greater than ${relatedValue}!`
          }
          break
        case FIELD_CONSTRAINTS.GTE:
          if (!constraintValidationWithOperation(value, relatedValue, FIELD_CONSTRAINTS.GTE)) {
            errorMessage = `${field.field_template_name} must be greater than or equal to ${relatedValue}!`
          }
          break

        default:
          console.log(`implementation for '${validation.constraint}' is missing`)
      }

      if (errorMessage) {
        validationArray.push(errorMessage)
        errorMessage = null
      }
    }
  }

  const finalErrorText = validationArray.join(', ')
  return getError(field, suffix, !!finalErrorText ? finalErrorText.trim() : null)
}

const getError = (field, suffix, text) => {
  return {
    [`${getConstraintErrorId(field.id)}${suffix}`]: text,
  }
}

export const getDefaultValueForField = (field) => {
  switch (field.data_structure) {
    case FIELD_TYPES.LIST:
    case FIELD_TYPES.LIST_MULTI:
      return -1
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.CURRENCY:
      return 0
    case FIELD_TYPES.TABLE:
      return 1
    default:
      return ''
  }
}

const getRelatedFieldOrConstantValue = (key, allValues, relatedFieldTemplateId, constantValue) => {
  let returnValue = null
  if (key.includes('__')) {
    let rowNumber = key.split('__')[1]
    if (relatedFieldTemplateId != null && constantValue == null) {
      returnValue = allValues['f_' + relatedFieldTemplateId + '__' + rowNumber]
    } else if (relatedFieldTemplateId == null && constantValue != null) {
      returnValue = constantValue
    }
  } else {
    if (relatedFieldTemplateId != null && constantValue == null) {
      returnValue = allValues['f_' + relatedFieldTemplateId]
    } else if (relatedFieldTemplateId == null && constantValue != null) {
      returnValue = constantValue
    }
  }
  return returnValue
}

export const requiredFieldValidation = (
  user,
  fields,
  requiredFields,
  conditionalEnabling,
  allValues,
  selectableTableData,
) => {
  let constraintValidation = []

  const idFieldMap = fields.reduce((acc, cur) => {
    return { ...acc, [cur.id]: cur }
  }, {})

  const idFieldMapRequired = requiredFields.reduce((acc, cur) => {
    return { ...acc, [cur.id]: cur }
  }, {})

  Object.entries(allValues).forEach(([key, value]) => {
    if (!key.startsWith('f_')) return

    // check if key is still required or not
    const { id, row } = getFieldIdFromKey(key)
    const field = idFieldMapRequired[id]

    if (field) {
      const defaultVal = getDefaultValueForField(field)
      // TREAT NULL/UNDEFINED AS EMPTY
      // File fields in some tables are stored as null when empty, causing validation to be skipped.
      const isEmpty = value === defaultVal || value === null || value === undefined

      if (isEmpty) {
        // field is marked as required and have default/null value
        if (field?.is_subfield) {
          // check if table itself is disabled, if so, return
          const tableField = fields.find((it) => it.id === field.table_field_template_id)
          const tableItselfConditionallyDisabled = !isConditionallyEnabled(
            field.table_field_template_id,
            allValues,
            conditionalEnabling,
            fields,
          )

          if (
            tableItselfConditionallyDisabled ||
            tableField?.is_hidden ||
            tableField?.is_read_only
          ) {
            return
          }
        }

        const conditionallyDisabled = !isConditionallyEnabled(
          id,
          allValues,
          conditionalEnabling,
          fields,
          row,
        )

        let cellFromUnselectedRow = false
        if (
          field?.is_subfield &&
          selectableTableData[getSelectableTableId(field?.table_field_template_id)]
        ) {
          const selectedRows =
            selectableTableData[getSelectableTableId(field?.table_field_template_id)]?.map(Number)
          cellFromUnselectedRow = !selectedRows?.includes(String(row))
        }

        let isFieldVisible = true
        const visibleTeams = field.visible_team_id.filter((it) => it != null)
        const visibleTeamTypes = field.visible_team_type.filter((it) => it != null)

        if (visibleTeams?.length > 0 || visibleTeamTypes?.length > 0) {
          // user in restricted to team or team_type
          const isVisibleBasedOnTeam = visibleTeams?.some((teamId) => user.teams.includes(teamId))
          const isVisibleBasedOnTeamType = visibleTeamTypes?.some((teamType) =>
            user.team_types.includes(teamType),
          )

          isFieldVisible = isVisibleBasedOnTeam || isVisibleBasedOnTeamType
        }

        const finallyDisabled =
          field.is_hidden ||
          conditionallyDisabled ||
          field.is_read_only ||
          field.is_imported ||
          field.is_derived ||
          field.is_computed ||
          cellFromUnselectedRow ||
          !isFieldVisible

        if (!finallyDisabled) {
          constraintValidation.push({
            [getConstraintErrorIdFromFieldId(key)]: row
              ? `${idFieldMap[field?.table_field_template_id]?.field_template_name}/${
                  field.field_template_name
                } is a required field.`
              : `${field.field_template_name}" is a required field.`,
          })
        }
      }
    }
  })

  return constraintValidation
}

export const finalConstraintsValidation = async (
  tableConstraints,
  fieldConstraints,
  allSubfields,
  listDataMetadata,
  allValues,
  selectableTableData,
  user,
  processId,
) => {
  let constraintValidation = []

  /* ========================== table field validations ========================= */
  for (let tableConstraint of tableConstraints) {
    const subFieldTemplateId = tableConstraint.subfield_template_id
    const tableFieldTemplateName = tableConstraint.table_name
    const isTableFieldSelectable = tableConstraint.is_selectable_table
    const subFieldTemplateName = tableConstraint.subfield_name
    const pivotFieldTemplateId = tableConstraint.pivot_field_template_id

    const constantValue = tableConstraint.constant_value
    const constraint = tableConstraint.constraint

    if (pivotFieldTemplateId) {
      try {
        const result = (
          await processDataService.tableSubfieldValidationAcrossProcess(
            user,
            tableConstraint.id,
            processId,
          )
        ).data

        constraintValidation = [...constraintValidation, ...result]
      } catch (error) {
        console.error(error.message)
      }
    } else {
      let allSubfieldValues = Object.entries(allValues).filter(([key, value]) => {
        return key.includes(getFieldId(subFieldTemplateId) + '__')
      })

      if (isTableFieldSelectable) {
        let selectedRows = selectableTableData['st_' + tableConstraint.table_field_template_id]
        allSubfieldValues = Object.entries(allValues).filter(([key, value]) => {
          if (key.includes(getFieldId(subFieldTemplateId) + '__')) {
            let rowNum = key.split('__')[1]
            if (selectedRows?.includes(rowNum)) {
              return key.includes(getFieldId(subFieldTemplateId) + '__')
            }
          }
        })
      }

      let tempValues = []

      if (constraint == TABLE_FIELD_CONSTRAINTS.UNIQUE) {
        for (const [key, value] of allSubfieldValues) {
          if (tempValues.includes(value)) {
            constraintValidation.push({
              [getConstraintErrorIdFromFieldId(key)]:
                `"${tableFieldTemplateName} / ${subFieldTemplateName}" must be unique across table.`,
            })
          }
          tempValues.push(value)
        }
      }

      if (constraint == TABLE_FIELD_CONSTRAINTS.MIN_1_MATCH) {
        // check if it has at least one value or not
        let isValue = allSubfieldValues.some(([key, value]) => value == constantValue)
        let matchText = listDataMetadata.find(
          (it) => it.list_data_id == constantValue,
        )?.list_data_value

        const message =
          matchText == null || matchText.trim() == ''
            ? `"${tableFieldTemplateName}/${subFieldTemplateName}" must have at least one required value.`
            : `"${tableFieldTemplateName}/${subFieldTemplateName}" must have at least one value of "${matchText}".`

        if (isValue === false) {
          constraintValidation.push({ [getConstraintErrorId(subFieldTemplateId)]: message })
        }
      }
    }
  }

  /* ========================== normal field validations =========================== */

  /* 
    loop through each fields
    collect all possible values in case of subfield only if associatedConstraints is there
    call @checkFieldConstraints for each value to check if there is any violation,
    and finally push the error to constraintValidation and return
  */

  const fields = allSubfields.map((ft) => {
    if (ft.is_subfield) {
      const tableField = allSubfields.find((it) => it.id === ft?.table_field_template_id)
      return {
        ...ft,
        field_template_name: `"${tableField?.field_template_name} / ${ft?.field_template_name}"`,
      }
    } else {
      return ft
    }
  })

  for (const field of fields) {
    const associatedConstraints = fieldConstraints.filter(
      (it) => it?.field_template_id === field.id,
    )
    if (associatedConstraints.length <= 0) {
      continue
    }

    const allFieldValues = Object.entries(allValues).reduce((acc, [key, value]) => {
      const keyDetails = getFieldIdFromKey(key)
      if (keyDetails?.id === field.id) {
        return [...acc, { ...keyDetails, value }]
      }
      return acc
    }, [])

    for (const { row, value } of allFieldValues) {
      const error = checkFieldConstraints(field, value, associatedConstraints, allValues, row)
      constraintValidation.push(error)
    }
  }

  return constraintValidation
}

export const getFieldForSetting = (columns = []) => {
  let columnsForSetting = []
  for (let i = 0; i < columns.length; i++) {
    columnsForSetting.push({
      path: '' + columns[i].id,
      name: columns[i].field_template_name,
      is_hidden: columns[i].is_hidden,
    })
  }
  return columnsForSetting
}

export const getColumnForSetting = (columns = []) => {
  let ActivityColumnsForSetting = []
  for (let i = 0; i < columns.length; i++) {
    ActivityColumnsForSetting.push({
      path: '' + columns[i].id,
      name: columns[i].activity_template_name,
      is_hidden: columns[i].is_hidden,
    })
  }
  return ActivityColumnsForSetting
}
